import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Copy, Save, Wand2, Scissors, Sparkles, Loader2, Bookmark, Image as ImageIcon, Download, RefreshCw, Lightbulb, Upload, X, Trash2, CalendarPlus } from "lucide-react";
import { Layout } from "../components/Layout";
import { Markdown } from "../components/Markdown";
import { api, formatError } from "../lib/api";
import { buildProfileContext } from "../lib/profile";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const TYPES = [
  ["facebook", "Publication Facebook"], ["instagram", "Publication Instagram"], ["story", "Story"],
  ["whatsapp", "Message WhatsApp"], ["sms", "SMS commercial"], ["email", "Email"],
  ["promotion", "Idée de promotion"], ["review_reply", "Réponse à un avis client"],
  ["animation", "Animation commerciale"], ["team_action", "Fiche action équipe"],
  ["sales_pitch", "Argumentaire de vente"], ["slogan", "Slogan"], ["cta", "Appel à l'action"],
];
const TONES = ["professionnel", "chaleureux", "dynamique", "premium", "humoristique"];
const VISUAL_FORMATS = [
  ["instagram", "Post Instagram"], ["facebook", "Visuel Facebook"], ["story", "Story"],
  ["promotion", "Affiche promotion"], ["event", "Affiche événement"], ["product", "Mise en avant produit"],
];
const CAL_TYPE = { facebook: "publication", instagram: "publication", story: "publication", whatsapp: "campagne", sms: "campagne", email: "campagne", promotion: "promotion", animation: "animation" };
const CAL_CHANNEL = { facebook: "Facebook", instagram: "Instagram", story: "Instagram", whatsapp: "WhatsApp", sms: "SMS", email: "Email", promotion: "Multi-canal", animation: "Boutique" };

export default function ContentStudio() {
  const { data, user, demo, pendingContent, setPendingContent, addCalendarEvent } = useApp();
  const [type, setType] = useState("instagram");
  const [tone, setTone] = useState("chaleureux");
  const [topic, setTopic] = useState("");
  const [why, setWhy] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState([]);
  // visual
  const [showVisual, setShowVisual] = useState(false);
  const [visualFormat, setVisualFormat] = useState("instagram");
  const [visualImg, setVisualImg] = useState("");
  const [visualLoading, setVisualLoading] = useState(false);
  const [refImage, setRefImage] = useState("");
  // schedule
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedDay, setSchedDay] = useState(String(Math.min(28, new Date().getDate() + 1)));
  const biz = data?.onboarding?.business_name || "mon commerce";
  const sector = data?.onboarding?.activity_type || "";
  const isAccount = user && !demo;

  useEffect(() => {
    if (pendingContent) {
      if (pendingContent.content_type) setType(pendingContent.content_type);
      if (pendingContent.topic) setTopic(pendingContent.topic);
      if (pendingContent.why) setWhy(pendingContent.why);
      setPendingContent(null);
      toast.info("Action préremplie depuis votre plan — cliquez sur Générer.");
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (isAccount) api.get("/deliverables").then(({ data: r }) => setSaved(r.items || [])).catch(() => {});
  }, [isAccount]);

  const run = async (action) => {
    if (action !== "generate" && !output) { toast.error("Générez d'abord un contenu."); return; }
    setLoading(true);
    try {
      const { data: res } = await api.post("/ai/content", {
        content_type: type, tone, topic, business_name: biz, action, previous: output, profile_context: buildProfileContext(data),
      });
      setOutput(res.content);
      toast.success(action === "generate" ? "Contenu créé !" : action === "improve" ? "Contenu amélioré !" : "Contenu raccourci !");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error("Erreur de génération", { description: detail ? formatError(detail) : "Le service IA est momentanément indisponible. Réessayez dans quelques instants." });
    }
    finally { setLoading(false); }
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("Photo trop lourde (max 6 Mo)."); return; }
    const r = new FileReader();
    r.onload = () => setRefImage(r.result);
    r.readAsDataURL(f);
  };

  const generateVisual = async () => {
    if (visualLoading) return;
    setVisualLoading(true); setVisualImg("");
    try {
      const { data: res } = await api.post("/ai/visual", {
        channel: visualFormat, objective: why || topic, content: output, business_name: biz, sector,
        profile_context: buildProfileContext(data), reference_image: refImage || "",
      });
      setVisualImg(res.image);
      toast.success("Visuel créé !");
    } catch { toast.error("La génération du visuel a échoué. Réessayez."); }
    finally { setVisualLoading(false); }
  };

  const downloadVisual = () => {
    const a = document.createElement("a");
    a.href = visualImg; a.download = `localboost-visuel-${Date.now()}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(output); toast.success("Copié !"); }
    catch { toast.error("Impossible de copier automatiquement."); }
  };

  const save = async () => {
    const payload = { type, title: (topic || output.slice(0, 60)), text: output, image: visualImg || "" };
    if (isAccount) {
      try { const { data: d } = await api.post("/deliverables", payload); setSaved((s) => [d, ...s]); toast.success("Enregistré !"); }
      catch { toast.error("Échec de l'enregistrement."); }
    } else {
      setSaved((s) => [{ id: `local_${Date.now()}`, ...payload, has_image: !!payload.image, created_at: new Date().toISOString() }, ...s]);
      toast.success("Enregistré !");
    }
  };

  const loadSaved = async (s) => {
    setType(s.type || "instagram"); setOutput(s.text || ""); setVisualImg("");
    if (s.image) { setVisualImg(s.image); setShowVisual(true); }
    else if (s.has_image && isAccount) {
      try { const { data: full } = await api.get(`/deliverables/${s.id}`); if (full.image) { setVisualImg(full.image); setShowVisual(true); } }
      catch (e) { console.error("Chargement du visuel échoué:", e); }
    }
    toast.info("Livrable chargé.");
  };

  const deleteSaved = async (id, e) => {
    e.stopPropagation();
    if (isAccount && !id.startsWith("local_")) {
      try { await api.delete(`/deliverables/${id}`); }
      catch (err) { console.error("Suppression du livrable échouée:", err); }
    }
    setSaved((s) => s.filter((x) => x.id !== id));
    toast.success("Supprimé.");
  };

  const schedule = () => {
    addCalendarEvent({ title: topic || biz, type: CAL_TYPE[type] || "tache", channel: CAL_CHANNEL[type] || "Multi-canal", day: Number(schedDay) });
    setSchedOpen(false);
    toast.success("Ajouté au Smart Calendar !", { description: `Jour ${schedDay}` });
  };

  return (
    <Layout>
      <div className="mb-6"><h1 className="font-display font-black text-2xl lg:text-3xl">Studio de création</h1><p className="text-sm text-[hsl(var(--muted-foreground))]">Transformez une recommandation en contenu réel, prêt à utiliser.</p></div>

      {why && (
        <div className="lb-card p-4 mb-4 border border-neon/20 bg-neon/[0.05]" data-testid="content-why">
          <p className="text-[11px] font-semibold text-neon mb-1 flex items-center gap-1"><Lightbulb size={13} /> Pourquoi cette action ?</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">{why}</p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Controls */}
        <div className="lb-card p-5 space-y-4" data-testid="content-controls">
          <div><Label className="text-xs">Que voulez-vous créer ?</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="content-type" className="mt-1.5 bg-white/5 border-white/10 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Ton</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {TONES.map((tn) => (
                <button key={tn} data-testid={`tone-${tn}`} onClick={() => setTone(tn)}
                  className={`px-3 min-h-[40px] sm:min-h-0 py-1.5 rounded-full text-xs capitalize transition-colors ${tone === tn ? "bg-primary text-white" : "bg-white/5 text-[hsl(var(--muted-foreground))] hover:text-white"}`}>{tn}</button>
              ))}
            </div>
          </div>
          <div><Label className="text-xs">Que souhaitez-vous mettre en avant ?</Label>
            <Input data-testid="content-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Ex : nouvelle carte d'automne" className="mt-1.5 bg-white/5 border-white/10 h-11" />
          </div>
          <Button data-testid="content-generate" onClick={() => run("generate")} disabled={loading}
            className="w-full rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <><Sparkles size={16} className="mr-1" /> CRÉER MON CONTENU</>}
          </Button>
          {saved.length > 0 && (
            <div className="pt-3 border-t border-white/5">
              <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1"><Bookmark size={12} /> Livrables enregistrés ({saved.length})</p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="saved-list">
                {saved.map((s) => (
                  <div key={s.id} onClick={() => loadSaved(s)} data-testid={`saved-${s.id}`}
                    className="group flex items-center gap-2 text-xs p-2 rounded-lg bg-white/[0.02] hover:bg-white/5 cursor-pointer transition-colors">
                    {s.has_image && <ImageIcon size={12} className="text-neon shrink-0" />}
                    <span className="flex-1 truncate text-[hsl(var(--muted-foreground))] group-hover:text-white">{s.title || s.text?.slice(0, 50)}</span>
                    <button data-testid={`saved-del-${s.id}`} onClick={(e) => deleteSaved(s.id, e)} className="text-[hsl(var(--muted-foreground))] hover:text-red-400 shrink-0"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Output */}
        <div className="lb-card p-5 flex flex-col" data-testid="content-output">
          <Label className="text-xs mb-2">Résultat</Label>
          <div className="flex-1 min-h-[240px] rounded-xl bg-white/[0.02] border border-white/5 p-4 text-sm overflow-y-auto">
            {loading ? (
              <div className="space-y-2"><div className="skeleton h-4 w-3/4" /><div className="skeleton h-4 w-full" /><div className="skeleton h-4 w-5/6" /><div className="skeleton h-4 w-2/3" /></div>
            ) : output ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}><Markdown>{output}</Markdown></motion.div>
            ) : (
              <p className="text-[hsl(var(--muted-foreground))]">Votre contenu apparaîtra ici. Choisissez ce que vous voulez créer, puis lancez la création.</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button data-testid="content-regenerate" onClick={() => run("generate")} disabled={loading || !output} variant="outline" className="rounded-full border-white/10 bg-white/5 text-xs h-11 sm:h-9"><RefreshCw size={14} className="mr-1" /> RÉGÉNÉRER</Button>
            <Button data-testid="content-improve" onClick={() => run("improve")} disabled={loading || !output} variant="outline" className="rounded-full border-white/10 bg-white/5 text-xs h-11 sm:h-9"><Wand2 size={14} className="mr-1" /> AMÉLIORER</Button>
            <Button data-testid="content-shorten" onClick={() => run("shorten")} disabled={loading || !output} variant="outline" className="rounded-full border-white/10 bg-white/5 text-xs h-11 sm:h-9"><Scissors size={14} className="mr-1" /> RACCOURCIR</Button>
            <Button data-testid="content-copy" onClick={copy} disabled={!output} variant="outline" className="rounded-full border-white/10 bg-white/5 text-xs h-11 sm:h-9"><Copy size={14} className="mr-1" /> COPIER</Button>
            <Button data-testid="content-save" onClick={save} disabled={!output} variant="outline" className="rounded-full border-white/10 bg-white/5 text-xs h-11 sm:h-9"><Save size={14} className="mr-1" /> ENREGISTRER</Button>
            <Button data-testid="content-schedule" onClick={() => setSchedOpen(true)} disabled={!output} variant="outline" className="rounded-full border-white/10 bg-white/5 text-xs h-11 sm:h-9"><CalendarPlus size={14} className="mr-1" /> PROGRAMMER</Button>
            <Button data-testid="content-visual-toggle" onClick={() => setShowVisual((v) => !v)} disabled={!output} className="rounded-full text-xs h-11 sm:h-9 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn"><ImageIcon size={14} className="mr-1" /> CRÉER LE VISUEL</Button>
          </div>

          {/* Visual creator */}
          {showVisual && output && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="visual-panel">
              <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                <div className="flex-1"><Label className="text-xs">Format du visuel</Label>
                  <Select value={visualFormat} onValueChange={setVisualFormat}>
                    <SelectTrigger data-testid="visual-format" className="mt-1.5 bg-white/5 border-white/10 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>{VISUAL_FORMATS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button data-testid="visual-generate" onClick={generateVisual} disabled={visualLoading} className="rounded-full h-11 px-4 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn">
                  {visualLoading ? <Loader2 size={18} className="animate-spin" /> : <><Sparkles size={16} className="mr-1.5" /> Générer le visuel</>}
                </Button>
              </div>

              {/* Optional real photo as base */}
              <div className="mt-3">
                {refImage ? (
                  <div className="flex items-center gap-2 text-xs">
                    <img src={refImage} alt="Photo importée" className="w-12 h-12 rounded-lg object-cover border border-white/10" />
                    <span className="text-[hsl(var(--muted-foreground))] flex-1">Photo réelle utilisée comme base du visuel.</span>
                    <button data-testid="visual-photo-remove" onClick={() => setRefImage("")} className="text-[hsl(var(--muted-foreground))] hover:text-red-400"><X size={15} /></button>
                  </div>
                ) : (
                  <label data-testid="visual-photo-label" className="inline-flex items-center gap-1.5 text-xs text-neon cursor-pointer hover:opacity-80">
                    <Upload size={13} /> Importer une photo (produit / boutique)
                    <input data-testid="visual-photo-input" type="file" accept="image/*" onChange={onPhoto} className="hidden" />
                  </label>
                )}
              </div>

              <div className="mt-3">
                {visualLoading ? (
                  <div className="skeleton w-full aspect-square max-w-sm mx-auto rounded-xl" />
                ) : visualImg ? (
                  <div className="space-y-3">
                    <img src={visualImg} alt="Visuel généré" className="w-full max-w-sm mx-auto rounded-xl border border-white/10" data-testid="visual-image" />
                    <div className="flex gap-2 justify-center">
                      <Button data-testid="visual-download" onClick={downloadVisual} variant="outline" className="rounded-full border-white/10 bg-white/5 text-xs h-11 sm:h-9"><Download size={14} className="mr-1" /> TÉLÉCHARGER</Button>
                      <Button data-testid="visual-regenerate" onClick={generateVisual} variant="outline" className="rounded-full border-white/10 bg-white/5 text-xs h-11 sm:h-9"><RefreshCw size={14} className="mr-1" /> AUTRE VERSION</Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-[hsl(var(--muted-foreground))] text-center py-4">Choisissez un format puis générez un visuel commercial à partir de votre contenu.</p>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Schedule to calendar */}
      <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
        <DialogContent className="glass border-white/10" data-testid="schedule-dialog">
          <DialogHeader><DialogTitle>Programmer la publication</DialogTitle></DialogHeader>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Ajoutez ce contenu à votre Smart Calendar.</p>
          <div className="grid grid-cols-2 gap-3 mt-1">
            <div><Label className="text-xs">Jour du mois</Label><Input data-testid="schedule-day" type="number" min="1" max="30" value={schedDay} onChange={(e) => setSchedDay(e.target.value)} className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
            <div><Label className="text-xs">Type</Label><Input value={CAL_TYPE[type] || "tache"} disabled className="mt-1.5 bg-white/5 border-white/10 h-11 capitalize" /></div>
          </div>
          <Button data-testid="schedule-confirm" onClick={schedule} className="w-full mt-2 rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn">Ajouter au calendrier</Button>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
